import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllCourses } from '../services/courseService';
import { getAllCategories } from '../services/categoryService';
import { resolveMediaUrl } from '../utils/media';
import './Courses.css';

function Courses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [courseModes, setCourseModes] = useState({});
  const [activeModalCourse, setActiveModalCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [coursesResponse, categoriesResponse] = await Promise.all([
        getAllCourses({ isActive: true }),
        getAllCategories({ isActive: true, sortBy: 'order', sortOrder: 'asc' })
      ]);

      if (coursesResponse.success) {
        setCourses(coursesResponse.data);
      }

      if (categoriesResponse.success) {
        setCategories([
          { _id: 'all', name: 'All Courses', slug: 'all' },
          ...categoriesResponse.data
        ]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const isOfflineAvailable = (course) => {
    return Boolean(course.isOfflineAvailable);
  };

  const getCourseMode = (courseId) => courseModes[courseId] || 'online';

  const toggleCourseMode = (courseId, mode, e) => {
    if (e) {
      if (typeof e.preventDefault === 'function') e.preventDefault();
      if (typeof e.stopPropagation === 'function') e.stopPropagation();
    }
    setCourseModes(prev => ({ ...prev, [courseId]: mode }));
  };

  const handleCardClick = (e, course) => {
    if (
      e.target.closest('.course-mode-selector') ||
      e.target.closest('.mode-pill') ||
      e.target.closest('.tc-link-btn') ||
      e.target.closest('.tc-modal-backdrop') ||
      e.target.closest('.tc-modal-card')
    ) {
      return;
    }
    const mode = getCourseMode(course._id);
    navigate(`/courses/${course.path}${mode === 'offline' ? '?mode=offline' : ''}`);
  };

  const getDisplayPricing = (course) => {
    const mode = getCourseMode(course._id);
    if (mode === 'offline' && isOfflineAvailable(course)) {
      const offlinePrice = course.offlinePrice || 10000;
      const offlineOrigPrice = course.offlineOriginalPrice || (course.offlinePrice ? course.offlinePrice : 15000);
      const discountPercent = offlineOrigPrice > offlinePrice
        ? Math.round(((offlineOrigPrice - offlinePrice) / offlineOrigPrice) * 100)
        : null;

      return {
        mode: 'offline',
        price: offlinePrice,
        originalPrice: offlineOrigPrice,
        discountPercent,
        discountLabel: discountPercent ? `UPTO ${discountPercent}% OFF` : null,
        isDiscounted: Boolean(discountPercent),
        tAndCLabel: '*Call for Discount Eligibility ℹ️'
      };
    }
    // Check if logged-in student has an approved custom online price for this course
    const userCustomOnline = (user && Array.isArray(user.customPricing))
      ? user.customPricing.find((p) => {
          const cid = typeof p.courseId === 'object' && p.courseId !== null ? p.courseId._id : p.courseId;
          return cid === course._id;
        })
      : null;

    if (userCustomOnline && typeof userCustomOnline.customPrice === 'number' && userCustomOnline.customPrice >= 0) {
      const customPrice = userCustomOnline.customPrice;
      const customDiscountPercent = course.price > customPrice
        ? Math.round(((course.price - customPrice) / course.price) * 100)
        : null;

      return {
        mode: 'online',
        price: customPrice,
        originalPrice: course.price,
        discountPercent: customDiscountPercent,
        discountLabel: customDiscountPercent ? `SCHOLARSHIP ${customDiscountPercent}% OFF` : '🏷️ CONCESSION APPLIED',
        isDiscounted: true,
        isCustomPrice: true,
        tAndCLabel: null
      };
    }

    const isDiscounted = course.originalPrice && course.originalPrice > course.price;
    const discountPercent = isDiscounted
      ? Math.round(((course.originalPrice - course.price) / course.originalPrice) * 100)
      : null;
    return {
      mode: 'online',
      price: course.price,
      originalPrice: course.originalPrice,
      discountPercent,
      discountLabel: discountPercent ? `UPTO ${discountPercent}% OFF` : null,
      isDiscounted,
      tAndCLabel: null
    };
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCourseImage = (course) => resolveMediaUrl(course.image) || '/backend_dev_image.jpeg';

  const filteredCourses = courses
    .filter(course => course.isActive !== false)
    .filter(course => activeCategory === 'all' || course.category?._id === activeCategory)
    .filter(course => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        course.title?.toLowerCase().includes(q) ||
        course.shortDescription?.toLowerCase().includes(q) ||
        course.instructor?.toLowerCase().includes(q) ||
        course.tags?.some(t => t.toLowerCase().includes(q))
      );
    });

  if (loading) {
    return (
      <section className="courses-section" id="courses">
        <div className="container">
          <div className="section-header text-center">
            <div className="section-tag">
              <span className="badge-pill badge-pill-amber">Curriculum Catalog</span>
            </div>
            <h2 className="section-title">Live Engineering Programs</h2>
            <p className="section-desc">Loading available cohorts...</p>
          </div>
          <div className="courses-loading-spinner" style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="courses-section" id="courses">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">Live Engineering Programs</h2>
            <p className="section-desc error-message" style={{ color: 'var(--danger-600)' }}>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={fetchData} style={{ marginTop: '16px' }}>
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="courses-section" id="courses">
      <div className="container">
        <div className="section-header text-center">
          <div className="section-tag">
            <span className="badge-pill badge-pill-amber">Curriculum Catalog</span>
          </div>
          <h2 className="section-title">Live Engineering Programs</h2>
          <p className="section-desc">
            Explore our specialized cohorts across systems programming, backend architectures, database reliability, and cloud platforms.
          </p>
        </div>

        {/* Filter & Search Controls */}
        <div className="catalog-controls">
          {categories.length > 0 && (
            <div className="filter-tabs-wrapper" role="tablist">
              {categories.map(category => (
                <button
                  key={category._id}
                  type="button"
                  className={`filter-btn ${activeCategory === category._id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category._id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          <div className="catalog-search-wrapper">
            <span className="search-icon-inside">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search cohorts, skills, tech..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="catalog-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="catalog-search-clear"
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Courses Cards Grid */}
        <div className="courses-grid" id="courses-grid">
          {filteredCourses.length > 0 ? (
            filteredCourses.map(course => {
              const pricing = getDisplayPricing(course);
              const isOffline = isOfflineAvailable(course);
              const currentMode = getCourseMode(course._id);

              return (
                <article
                  key={course._id}
                  className="course-card"
                  onClick={(e) => handleCardClick(e, course)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="course-thumb-wrap">
                    <img
                      src={getCourseImage(course)}
                      alt={course.title}
                      className="course-image"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = '/backend_dev_image.jpeg'; }}
                    />
                    <div className="course-thumb-overlay"></div>
                    <span className="course-badge badge-live">
                      {isOffline && currentMode === 'offline' ? 'Classroom Cohort' : 'Live Cohort'}
                    </span>
                    <div className="course-level-tag">
                      {course.level || 'Intermediate'}
                    </div>
                  </div>

                  <div className="course-card-body">
                    {/* Learning Mode Switcher for Offline Enabled Courses */}
                    {isOffline && (
                      <div
                        className="course-mode-selector"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      >
                        <span className="mode-label">Mode:</span>
                        <div className="mode-pill-group">
                          <button
                            type="button"
                            className={`mode-pill ${currentMode === 'online' ? 'active' : ''}`}
                            onClick={(e) => toggleCourseMode(course._id, 'online', e)}
                          >
                            🌐 Online
                          </button>
                          <button
                            type="button"
                            className={`mode-pill ${currentMode === 'offline' ? 'active' : ''}`}
                            onClick={(e) => toggleCourseMode(course._id, 'offline', e)}
                          >
                            🏢 Offline
                          </button>
                        </div>
                      </div>
                    )}

                    {isOffline && currentMode === 'offline' && (
                      <div className="course-venue-tag">
                        <span>📍 Center: Jaipur Campus</span>
                      </div>
                    )}

                    <div className="course-meta-top">
                      <span className="meta-item">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {course.duration || '8 Weeks'}
                      </span>
                      <span className="meta-item meta-rating">
                        ★ {course.rating || 4.9}
                      </span>
                      <span className="meta-seats-pill">30 seats max</span>
                    </div>

                    <h3 className="course-title">{course.title}</h3>
                    <p className="course-description">
                      {course.shortDescription || 'Master real-world distributed architectures with weekly mentor pull request reviews.'}
                    </p>

                    {/* Tech Tags */}
                    {course.tags && course.tags.length > 0 && (
                      <div className="course-tech-tags">
                        {course.tags.flatMap(t => t.split(',')).slice(0, 4).map((t, idx) => (
                          t.trim() && <span className="tech-tag" key={idx}>{t.trim()}</span>
                        ))}
                      </div>
                    )}

                    <div className="course-card-footer">
                      <div className="course-price-wrap">
                        <span className="current-price">{formatPrice(pricing.price)}</span>
                        {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                          <span className="orig-price">{formatPrice(pricing.originalPrice)}</span>
                        )}
                        {pricing.discountLabel && (
                          <span className="discount-pill-tag">{pricing.discountLabel}</span>
                        )}
                      </div>

                      <div className="course-actions">
                        {pricing.tAndCLabel ? (
                          <button
                            type="button"
                            className="tc-link-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveModalCourse(course);
                            }}
                          >
                            Inquiry ℹ️
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${course.path}${currentMode === 'offline' ? '?mode=offline' : ''}`);
                          }}
                        >
                          Explore &rarr;
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="no-courses-found" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              <p>No cohorts found matching your criteria.</p>
            </div>
          )}
        </div>

        <div className="view-all-container" style={{ textAlign: 'center', marginTop: '40px' }}>
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={() => navigate('/courses')}
          >
            Explore All Cohorts &rarr;
          </button>
        </div>
      </div>

      {/* Classroom Batch & Discount Inquiry Modal */}
      {activeModalCourse && (
        <div className="tc-modal-backdrop" onClick={() => setActiveModalCourse(null)}>
          <div className="tc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="tc-modal-header">
              <div className="tc-modal-title-group">
                <span className="tc-modal-kicker">Classroom Training Program</span>
                <h3 className="tc-modal-title">Offline Batch &amp; Discount Inquiry</h3>
                <p className="tc-modal-subtitle">
                  {activeModalCourse.title} • Mentor: {activeModalCourse.instructor}
                </p>
              </div>
              <button
                type="button"
                className="tc-modal-close"
                onClick={() => setActiveModalCourse(null)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="tc-modal-body">
              <div className="discount-call-notice">
                <div className="notice-badge">Special Concession: UPTO 33% OFF</div>
                <div className="pricing-summary-row">
                  <span className="concession-price">Starting from ₹10,000</span>
                  <span className="standard-price">Standard Fee: ₹15,000</span>
                </div>
                <p className="notice-desc">
                  A special fee concession of <strong>UPTO 33%</strong> is available for classroom batches at our Jaipur campus. Discount eligibility and final fee approval are evaluated during counseling based on student profile and seat availability.
                </p>
              </div>

              <div className="venue-info-box">
                <div className="venue-header">
                  <span className="venue-icon">📍</span>
                  <h4 className="venue-title">Classroom Training Venue</h4>
                </div>
                <p className="venue-address">
                  P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706
                </p>
                <div className="venue-meta">
                  <span>🏢 In-Person Practical Labs</span>
                  <span>👨‍🏫 Direct Mentor Mentorship</span>
                </div>
              </div>

              <div className="inquiry-contact-card">
                <div className="contact-heading">Speak to Admissions Desk:</div>
                <p className="contact-subtext">
                  Call or message our counseling team now to verify your discount eligibility and confirm batch timings:
                </p>
                <div className="contact-actions-row">
                  <a href="tel:+917852034945" className="btn btn-secondary btn-sm">
                    📞 Call: +91 7852034945
                  </a>
                  <a
                    href={`https://wa.me/917852034945?text=${encodeURIComponent(`Hi Durbhasi Gurukulam, I am interested in the Offline Classroom Batch for ${activeModalCourse.title} and want to check my eligibility for the offline batch discount.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    💬 WhatsApp Desk
                  </a>
                </div>
              </div>
            </div>

            <div className="tc-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setActiveModalCourse(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  const path = activeModalCourse.path;
                  setActiveModalCourse(null);
                  navigate(`/courses/${path}?mode=offline`);
                }}
              >
                View Full Course Details →
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Courses;
